import { Component, OnInit } from '@angular/core';
import { LayoutModule } from '@angular/cdk/layout';
import { ActivatedRoute, NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { NavbarModule } from '../navbar/navbar.module';
import { SvgIconComponent } from 'src/app/shared/ui/svg-icon/svg-icon.component';
import { NavHeaderComponent } from '../nav-header/nav-header.component';
import { AppState } from 'src/app/core/app.state';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ICartItem } from 'src/app/features/catalogs/models/catalogs.models';
import { BASE_URL } from 'src/environments/environment';
import { CommonModule } from '@angular/common';
import { AmountCurrencyPipe } from 'src/app/shared/pipes/amount-currency.pipe';
import { ObserversModule } from '@angular/cdk/observers';
import { IProduct, IVariant, IHandlingUnit } from 'src/app/shared/models/products.models';
import { LoadCart } from 'src/app/features/catalogs/store/catalogs.actions';
import { ICurrency } from 'src/app/features/auth/models/auth-response';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    LayoutModule,
    NavbarModule,
    RouterModule,
    RouterOutlet,
    SvgIconComponent,
    NavHeaderComponent,
    CommonModule,
    AmountCurrencyPipe,
    ObserversModule,
    TranslateModule,
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
})
export class MainLayoutComponent implements OnInit {
  BASE_URL = BASE_URL;

  cartItems$!: Observable<ICartItem[]>;

  cartTotalItems = 0;
  cartTotalUnits = 0;
  cartTotalPrice = 0;

  cartsTotalPricePerCurrency: { total: number; currency: ICurrency | undefined; }[] = [];

  activeRoute = '';

  destroy$ = new Subject<void>();
  year: number = new Date().getFullYear();

  currency: ICurrency = LocalStorageService.get('currentUser').location.currency;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.cartItems$ = this.store.select((state) => state.catalogs.cart);
  }

  ngOnInit(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.activeRoute = event.url;
      }
    });

    const currentNavigation = this.router.getCurrentNavigation();
    if (!currentNavigation) {
      this.activeRoute = this.router.url;
    }

    this.cartItems$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
      if (items.length && items.map((item) => item.products).flat().length) {
        this.cartTotalItems = items.map((item) => item.products).flat().length;
        
        const previousCartTotalUnits = this.cartTotalUnits;
        this.cartTotalUnits = items
          .map((item) =>
            item.products && item.products.length ? item.products : []
          )
          .flat()
          .reduce((acc, product) => {
            // qte_selected est déjà en unités totales (total units)
            const totalUnits = product.qte_selected || 0;
            return acc + totalUnits;
          }, 0);

        this.cartTotalPrice = items
          .map((item) => item.products && item.products.length ? item.products : [])
          .flat()
          .reduce((acc, item) => {
            // qte_selected est déjà en unités totales (total units)
            const totalUnits = item.qte_selected || 0;
            const finalPrice = this.getFinalPrice(item);
            const lineTotal = finalPrice * totalUnits;
            return acc + lineTotal;
          }, 0);

        const currencyTotalsMap = new Map<string, { total: number; currency: ICurrency | undefined }>();

        items.forEach((item) => {
          const currencyKey = item.currency?.name || 'unknown';
          let itemTotal = item.products && item.products.length
            ? item.products.reduce((acc, prod) => {
                // qte_selected est déjà en unités totales (total units)
                const totalUnits = prod.qte_selected || 0;
                const finalPrice = this.getFinalPrice(prod);
                const lineTotal = finalPrice * totalUnits;
                return acc + lineTotal;
              }, 0)
            : 0;

          if(item.best_deal && item.best_deal.length) {
            // Apply formula: catalog.price * ( (1-rule1%) * (1-rule2%) * (1-ruleN%) )
            const discountMultiplier = item.best_deal.reduce((multiplier, deal) => {
              const discountPercent = Number(deal.extra_discount) || 0;
              return multiplier * (1 - discountPercent / 100);
            }, 1);

            itemTotal = Number((itemTotal * discountMultiplier).toFixed(2));
          }

          if (currencyTotalsMap.has(currencyKey)) {
            const entry = currencyTotalsMap.get(currencyKey)!;
            entry.total += itemTotal;
          } else {
            currencyTotalsMap.set(currencyKey, {
              total: itemTotal,
              currency: item.currency
            });
          }
        });

        this.cartsTotalPricePerCurrency = Array.from(currencyTotalsMap.values());
      } else {
        this.cartTotalItems = 0;
        this.cartTotalUnits = 0;
        this.cartTotalPrice = 0;
        this.cartsTotalPricePerCurrency = [];
      }
    });

    this.store.dispatch(LoadCart());
  }

  getFinalPrice(item: IProduct | IVariant): number {
    if(item.best_deal && item.best_deal.length) {
      return this.getFinalPriceAfterDeal(item);
    }

    return item.final_price ? item.final_price : item.sales_price * (1 - item.discount! / 100);
  }

  getFinalPriceAfterDeal(product: IProduct | IVariant): number {
    if(!product.best_deal || !product.best_deal.length) return 0;
    
    const finalPrice = product.final_price
      ? product.final_price
      : product.sales_price * (1 - product.discount! / 100);

    // Apply formula: catalog.price * ( (1-rule1%) * (1-rule2%) * (1-ruleN%) )
    const discountMultiplier = product.best_deal.reduce((multiplier, deal) => {
      const discountPercent = Number(deal.extra_discount) || 0;
      return multiplier * (1 - discountPercent / 100);
    }, 1);

    return Number((finalPrice * discountMultiplier).toFixed(2));
  }

  goToCart() {
    this.router.navigate(['/cart']);
  }

  logout() {
    localStorage.removeItem('currentUser');
    this.router.navigate(['']);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
